import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CheckCircle2, XCircle, ChevronDown, X } from 'lucide-react';
import FormFolderEdit, { FORM_FOLDER_EDIT_ID } from '../forms/FormFolderEdit';
import { getStatusOptions } from '../forms/FormFolderCreate';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

function getStatusDisplay() {
    return {
        '': { label: i18n.t('folders:sansStatut'), icon: null, className: 'text-muted-foreground' },
        open: { label: i18n.t('folders:ouvert'), icon: CheckCircle2, className: 'text-secondary' },
        closed: { label: i18n.t('folders:ferme'), icon: XCircle, className: 'text-muted-foreground' },
    };
}

export default function SheetFolderEdit({
    open,
    onClose,
    onSubmit,
    item,
    itemType = 'folder',
    users,
    isSubmitting = false,
}) {
    const { t } = useTranslation();
    const STATUS_OPTIONS = getStatusOptions();
    const STATUS_DISPLAY = getStatusDisplay();
    const [status, setStatus] = useState('');

    // Sync status when item changes
    useEffect(() => {
        setStatus(item?.status ?? '');
    }, [item]);

    const getTitle = () => {
        switch (itemType) {
            case 'folder':
                return t('folders:modifierLeDossier');
            case 'intervention':
                return t('folders:modifierLeSousDossier');
            case 'photo':
                return t('folders:modifierLaPhoto');
            case 'procedure_report':
                return t('folders:modifierLeProcesVerbal');
            default:
                return t('folders:modifier');
        }
    };

    const showStatusDropdown = itemType === 'folder' || itemType === 'intervention';
    const currentStatus = STATUS_DISPLAY[status] || STATUS_DISPLAY[''];
    const StatusIcon = currentStatus.icon;

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <SheetContent showCloseButton={false} className="w-[90vw] md:w-[500px]! md:max-w[500px]! flex flex-col p-0">
                <SheetHeader className="px-5 py-5 border-b border-border shrink-0">
                    <div className="flex items-center justify-between gap-3">
                        <SheetTitle className="text-lg font-semibold text-foreground">{getTitle()}</SheetTitle>
                        <div className="flex items-center gap-3">
                            {showStatusDropdown && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                                        >
                                            {StatusIcon && (
                                                <StatusIcon className={`h-4 w-4 ${currentStatus.className}`} />
                                            )}
                                            <span
                                                className={`text-[13px] ${status ? 'text-foreground' : 'text-muted-foreground'}`}
                                            >
                                                {currentStatus.label}
                                            </span>
                                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="min-w-[160px] rounded-xl p-0 overflow-hidden"
                                    >
                                        <DropdownMenuItem
                                            className="gap-3 px-4 py-3 text-sm text-muted-foreground cursor-pointer"
                                            onSelect={() => setStatus('')}
                                        >
                                            {t('folders:sansStatut')}
                                        </DropdownMenuItem>
                                        {STATUS_OPTIONS.map((opt) => {
                                            const display = STATUS_DISPLAY[opt.value];
                                            const Icon = display?.icon;
                                            return (
                                                <DropdownMenuItem
                                                    key={opt.value}
                                                    className="gap-3 px-4 py-3 text-sm cursor-pointer"
                                                    onSelect={() => setStatus(opt.value)}
                                                >
                                                    {Icon && <Icon className={`h-4 w-4 ${display.className}`} />}
                                                    {opt.label}
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted border border-border text-foreground hover:bg-muted/80 transition-colors shrink-0"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </SheetHeader>

                <FormFolderEdit item={item} itemType={itemType} onSubmit={onSubmit} users={users} status={status} />

                <SheetFooter className="px-6 py-4 border-t border-border flex gap-3">
                    <Button
                        type="button"
                        variant="soft"
                        color="neutral"
                        onClick={onClose}
                        className="flex-1"
                        disabled={isSubmitting}
                    >
                        {t('folders:annuler')}
                    </Button>
                    <SubmitButton
                        form={FORM_FOLDER_EDIT_ID}
                        className="flex-1 px-4 py-3 rounded-lg"
                        isSubmitting={isSubmitting}
                        loadingText={t('folders:miseAJour')}
                    >
                        {t('folders:mettreAJour')}
                    </SubmitButton>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
